import { Injectable } from "@angular/core";
import { BehaviorSubject, fromEvent, map, Subscription } from "rxjs";

import { MessageService } from "primeng/api";

import { Message } from "@bbwt/classes";
import { BroadcastService } from "@bbwt/modules/broadcasting";

@Injectable()
export class AppOnlineStateService {
    static readonly AppOnlineStateChanged = "AppOnlineStateChanged";
    private _isAppOnlineSubject = new BehaviorSubject<boolean>(navigator.onLine);

    private onlineEventSubscription: Subscription;
    private offlineEventSubscription: Subscription;

    public offlineAllowedRoutes = [
        "/app",
        "/app/consultation-history",
        "/app/walk-ins",
        "/app/patients",
        "/app/prescriptions/new",
        "/app/appointments",
    ];

    constructor(
        private messageService: MessageService,
        private broadcastService: BroadcastService) {
        if (navigator.onLine) return;
        setTimeout(this.showAppOfflineWarning, 3000);
    }

    get isAppOnline() {
        return this._isAppOnlineSubject.value;
    }

    get isAppOnline$() {
        return this._isAppOnlineSubject.asObservable();
    }

    get isAppOffline$() {
        return this.isAppOnline$.pipe(map(online => !online));
    }

    subscribeAppStateEvents() {
        this.subscribeOnlineEvent();
        this.subscribeOfflineEvent();
    }

    unsubscribeAppStateEvents() {
        this.onlineEventSubscription?.unsubscribe();
        this.offlineEventSubscription?.unsubscribe();
    }

    private subscribeOnlineEvent() {
        this.onlineEventSubscription = fromEvent(window, "online").subscribe(() => {
            this._isAppOnlineSubject.next(true);
            this.broadcastService.broadcast(AppOnlineStateService.AppOnlineStateChanged, { isAppOnline: true });
        });
    }

    private subscribeOfflineEvent() {
        this.offlineEventSubscription = fromEvent(window, "offline").subscribe(() => {
            this._isAppOnlineSubject.next(false);
            this.showAppOfflineWarning();
            this.broadcastService.broadcast(AppOnlineStateService.AppOnlineStateChanged, { isAppOnline: false });
        });
    }

    private showAppOfflineWarning() {
        this.messageService.add(Message.Warning("Application is in offline mode"));
    }
}
